import { parseDateSafe } from "@ews-admin/global-design-system";
import { getI18n } from "react-i18next";

export type DateValue = string | number[] | Date | null | undefined;

/**
 * The locale every formatter below renders in: the language react-i18next is
 * currently serving, NOT the browser's.
 *
 * MED-1475 — those two diverge constantly in production. Every call site used
 * to pass `undefined`, which means "the browser/OS locale", so a French UI in
 * an en-US browser rendered "9/29/2026", "Sep 29, 2026", "3:58:49 PM" and
 * "September 2026". Reading the language here, at call time rather than
 * caching it, is also what makes the rendered format follow the language
 * switch: components re-render on `languageChanged` and call straight back in.
 */
export function activeLocale(): string {
  const i18n = getI18n();
  return i18n?.resolvedLanguage || i18n?.language || "fr";
}

/**
 * Java LocalDate(Time) is serialised as [Y, M, D, ...] — format either that or an
 * ISO string. Shared by GrantHistorySection, GrantFreeAccessModal,
 * AuditLogSection, InvoiceManagement, DoctorInvitationsManagement and
 * LicencePromotionsManagement so the same kind of date renders consistently
 * wherever it's shown. Pass `includeTime` for timestamps where the
 * time-of-day matters (e.g. the audit log, where several actions can happen
 * on the same day).
 *
 * Parsing (array vs. date-only-string vs. full-ISO/Date/number, and the
 * off-by-one-day-in-a-timezone-behind-UTC bug that comes from naively
 * parsing a date-only string) is delegated to the design system's
 * parseDateSafe — don't reimplement it here, and in particular never format a
 * calendar date through UTC: that renders the previous day for every viewer
 * ahead of UTC.
 */
export function formatDateTime(
  value: DateValue,
  includeTime = false,
  locale: string = activeLocale()
): string {
  if (!value) return "—";
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(includeTime
      ? { hour: "2-digit" as const, minute: "2-digit" as const }
      : {}),
  };
  const d = parseDateSafe(value as string | number[] | Date);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(locale, options);
}

/**
 * Same as {@link formatDateTime}, but returns `null` instead of a placeholder
 * when there is nothing sensible to show — the value is missing, or it is not
 * a date at all.
 *
 * MED-1513: the invoices list printed the literal string "Invalid Date" under
 * every invoice number, because it fed `createdAt` (an ISO-8601 instant)
 * through `Number(...) * 1000` first. Callers that render a date as an
 * optional subtitle use this and drop the element on `null`, so a field the
 * API does not send can never surface as "Invalid Date" again.
 */
export function formatDateTimeOrNull(
  value: DateValue,
  includeTime = false,
  locale: string = activeLocale()
): string | null {
  if (!value) return null;
  const d = parseDateSafe(value as string | number[] | Date);
  if (isNaN(d.getTime())) return null;
  return formatDateTime(d, includeTime, locale);
}

/**
 * A day plus its wall-clock time down to the second — the invitation
 * timeline's "29/09/2026 15:58:49" (MED-1475), which used to render as the
 * en-US "9/29/2026, 3:58:49 PM".
 */
export function formatTimestamp(
  value: DateValue,
  locale: string = activeLocale()
): string {
  if (!value) return "—";
  const d = parseDateSafe(value as string | number[] | Date);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * A billing period ("2026-09", or a year/month pair) as a full month name —
 * "septembre 2026" in French, "September 2026" in English (MED-1475).
 */
export function formatMonthYear(
  value: string | null | undefined,
  locale: string = activeLocale()
): string {
  if (!value) return "—";
  const [year, month] = value.split("-").map(Number);
  if (!year || !month) return value;
  return new Date(year, month - 1).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
  });
}

/**
 * MED-1480: a "YYYY-MM" bucket as a short localized month — "sept. 2026" / "Sep 2026".
 * Short on purpose: these are chart axis ticks, where the full month name does not fit.
 */
export function formatMonthShort(
  month: string,
  locale: string = activeLocale()
): string {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return month;
  return new Date(year, monthNumber - 1).toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
  });
}

/**
 * MED-1480: a "YYYY-MM-DD" bucket as a short localized day — "29 sept." / "Sep 29".
 * Parsed through parseDateSafe, never through UTC: formatting a calendar date in UTC
 * renders the previous day for every viewer ahead of UTC.
 */
export function formatDayShort(
  day: string,
  locale: string = activeLocale()
): string {
  const parsed = parseDateSafe(day);
  if (isNaN(parsed.getTime())) return day;
  return parsed.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
}

/**
 * Turns the value of a date input ("1990-05-04") into the full instant the BFF
 * expects, or undefined when the field was left empty (MED-1423).
 *
 * The BFF types dateOfBirth as an Instant and registers no custom reader for
 * it, so a bare year-month-day string is rejected as an invalid payload.
 *
 * A date-only string is parsed as UTC midnight by the language itself, so this
 * does NOT shift the day the way building a local Date and serialising it back
 * would. Returning undefined rather than an empty string matters on update:
 * the API applies only the fields it receives, so omitting the key leaves an
 * existing birth date alone instead of clearing it.
 */
export function dateInputToInstant(value?: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/** Formats a stored instant back into the value a date input expects. */
export function instantToDateInput(value?: string): string {
  if (!value) return "";
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}
